import mission1Data from '@/assets/game/missions/mission_one/mission_one_db.json'
import mission2Data from '@/assets/game/missions/mission_2/mission_2_db.json'
import mission3Data from '@/assets/game/missions/mission_3/mission_3_db.json'
import mission4Data from '@/assets/game/missions/mission_4/mission_4_db.json'
import mission5Data from '@/assets/game/missions/mission_5/mission_5_db.json'
import mission6Data from '@/assets/game/missions/mission_6/mission_6_db.json'
import mission7Data from '@/assets/game/missions/mission_7/mission_7_db.json'
import mission8Data from '@/assets/game/missions/mission_8/mission_8_db.json'
import mission9Data from '@/assets/game/missions/mission_9/mission_9_db.json'
import mission10Data from '@/assets/game/missions/mission_10/mission_10_db.json'
import mission11Data from '@/assets/game/missions/mission_11/mission_11_db.json'
import mission12Data from '@/assets/game/missions/mission_12/mission_12_db.json'
import { auth } from '@/firebase/config'
import { resetMissionRunRewards } from '@/utils/missionRunRewards'

/**
 * Check if mission data exists for a given mission ID
 * @param {number|string} missionId - The ID of the mission to check
 * @returns {object|null} - Returns mission data if found, null if not found
 */
export const getMissionData = (missionId) => {
  // Convert to number to handle both string and number inputs
  const numericMissionId = parseInt(missionId)

  if (numericMissionId === 1) {
    return mission1Data
  } else if (numericMissionId === 2) {
    return mission2Data
  } else if (numericMissionId === 3) {
    return mission3Data
  } else if (numericMissionId === 4) {
    return mission4Data
  } else if (numericMissionId === 5) {
    return mission5Data
  } else if (numericMissionId === 6) {
    return mission6Data
  } else if (numericMissionId === 7) {
    return mission7Data
  } else if (numericMissionId === 8) {
    return mission8Data
  } else if (numericMissionId === 9) {
    return mission9Data
  } else if (numericMissionId === 10) {
    return mission10Data
  } else if (numericMissionId === 11) {
    return mission11Data
  } else if (numericMissionId === 12) {
    return mission12Data
  }
  return null
}

/**
 * Validate if a mission exists and is available
 * @param {number|string} missionId - The ID of the mission to validate
 * @returns {boolean} - Returns true if mission exists, false otherwise
 */
export const isMissionAvailable = (missionId) => {
  const missionData = getMissionData(missionId)
  return missionData !== null && missionData !== undefined
}

/**
 * Get mission title by ID from the mission data
 * @param {number|string} missionId - The ID of the mission
 * @returns {string|null} - Returns mission title if found, null otherwise
 */
export const getMissionTitle = (missionId) => {
  const missionData = getMissionData(missionId)
  return missionData ? missionData.title : null
}

/**
 * Handle mission navigation with validation
 * @param {number|string} missionId - Mission ID
 * @param {string} missionTitle - Mission title
 * @param {string} userId - User/Child ID
 * @param {string} userType - Type of user ('student' or 'child')
 * @param {string} [parentUserId] - Parent UID when userType is 'child' (for notes ownership)
 * @returns {boolean} - Returns true if navigation was successful, false if mission not found
 */
export const handleMissionNavigation = (
  missionId,
  missionTitle,
  userId,
  userType = 'student',
  parentUserId = null
) => {
  const missionData = getMissionData(missionId)

  if (!missionData) {
    throw new Error(`Mission ${missionId} not found`)
  }

  const missionContext = {
    missionId,
    missionTitle,
    userId,
    userType,
    timestamp: Date.now()
  }
  if (userType === 'child' && parentUserId) {
    missionContext.parentUserId = parentUserId
  }
  localStorage.setItem('currentMissionContext', JSON.stringify(missionContext))
  dispatchMissionContextChanged()
  resetMissionRunRewards()

  // Navigate based on user type
  const basePath =
    userType === 'child' ? '/dashboard/child' : '/dashboard/student'
  window.location.href = `${basePath}/game/mission/${missionId}`

  return true
}

// Custom event fired whenever currentMissionContext changes. Consumers like
// RewardContext and the active-child hook subscribe to this so they can
// re-resolve which user's reward doc to read without waiting for a full page
// navigation. Named under a project-scoped prefix to avoid collisions.
export const MISSION_CONTEXT_CHANGED_EVENT = 'digipalz:mission-context-changed'

function dispatchMissionContextChanged() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(MISSION_CONTEXT_CHANGED_EVENT))
}

/**
 * Set the mission context for a child picked from the profile selector or
 * the sidebar child-switcher. This used to only happen at mission-start time,
 * which left the DigiShop / Leaderboard pages reading the parent's reward doc
 * on first entry. Writing it at profile-pick time fixes that.
 *
 * @param {Object} child - child record (must have `childId`)
 * @param {string} parentUid - Firebase auth UID of the parent
 */
export const setChildMissionContext = (child, parentUid) => {
  if (!child?.childId || !parentUid) return
  const ctx = {
    userId: child.childId,
    userType: 'child',
    parentUserId: parentUid,
    timestamp: Date.now()
  }
  localStorage.setItem('currentMissionContext', JSON.stringify(ctx))
  dispatchMissionContextChanged()
}

/**
 * Remove child mission context only. Student mission context is kept so an
 * in-flight mission (and load logs) are not wiped when RewardContext mounts.
 */
export const clearChildMissionContext = () => {
  try {
    const raw = localStorage.getItem('currentMissionContext')
    if (!raw) return false
    const ctx = JSON.parse(raw)
    if (ctx?.userType !== 'child') return false
    localStorage.removeItem('currentMissionContext')
    dispatchMissionContextChanged()
    return true
  } catch {
    return false
  }
}

export const readStoredMissionContext = () => {
  try {
    const raw = localStorage.getItem('currentMissionContext')
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const readSelectedProfileChildId = () => {
  try {
    const raw = localStorage.getItem('selectedProfile')
    if (!raw) return null
    return JSON.parse(raw)?.childId ?? null
  } catch {
    return null
  }
}

/**
 * Resolves who is playing the mission for logs, progress, and instruction video.
 * Child: userId = parent auth uid, childId = child profile id, missionProfileId = child id.
 * Student: userId = student uid, childId = null, missionProfileId = student uid.
 */
export const resolveMissionUserContext = (authUid = null) => {
  const uid = authUid ?? auth.currentUser?.uid ?? null
  const ctx = readStoredMissionContext()
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname || '' : ''

  if (ctx?.userType === 'child' && ctx?.userId) {
    return {
      userType: 'child',
      userId: ctx.parentUserId || uid,
      childId: ctx.userId,
      missionProfileId: ctx.userId
    }
  }

  if (ctx?.userType === 'student' && ctx?.userId) {
    return {
      userType: 'student',
      userId: ctx.userId,
      childId: null,
      missionProfileId: ctx.userId
    }
  }

  if (ctx?.userId && ctx?.userType) {
    return {
      userType: ctx.userType,
      userId: ctx.userId,
      childId: null,
      missionProfileId: ctx.userId
    }
  }

  if (!uid) return null

  if (pathname.includes('/dashboard/child/')) {
    const childId = readSelectedProfileChildId()
    return {
      userType: 'child',
      userId: uid,
      childId,
      missionProfileId: childId
    }
  }

  if (pathname.includes('/dashboard/student/')) {
    return {
      userType: 'student',
      userId: uid,
      childId: null,
      missionProfileId: uid
    }
  }

  return {
    userType: 'unknown',
    userId: uid,
    childId: null,
    missionProfileId: uid
  }
}
